/**
 * 搜索结果 -> 可注入对话的引用文本（T12a）。
 *
 * 用途：把 `SearchResult[]` 渲染成一段带编号的纯文本，由上层作为
 * system / tool 消息注入对话，让模型能引用 `[1] [2]` 指代来源。
 *
 * 输出格式（固定，便于测试与后续做「点击 [n] 跳转」的前端解析）：
 * ```
 * [1] 标题
 * URL: https://...
 * 摘要：...
 *
 * [2] 标题
 * ...
 * ```
 *
 * 关键规则：
 * - 空结果 -> 返回空字符串（`''`），绝不返回只有表头的残骸；
 * - 标题 / URL 为空 -> 用占位符，避免出现「[1] \nURL: \n」这类空洞；
 * - 摘要超长 -> 按 `maxSnippetChars` 截断并追加省略号，且**按码点截断**
 *   （`Array.from` 计数），避免把 emoji / 代理对从中间劈开；
 * - `publishedAt` 存在时作为独立一行。
 */

import type { SearchResult } from './types';

/** 单条摘要默认最大字符数（码点）。超出部分截断。 */
export const DEFAULT_MAX_SNIPPET_CHARS = 240;

/** 截断后追加的省略号（单字符，避免占用过多 token）。 */
export const TRUNCATION_SUFFIX = '…';

export interface FormatSearchResultsOptions {
  /** 单条摘要最大字符数（按码点计）。默认 {@link DEFAULT_MAX_SNIPPET_CHARS}。 */
  maxSnippetChars?: number;
  /** 是否包含可选注入头（如「以下为联网搜索结果」）。默认 false。 */
  includeHeader?: boolean;
  /** 自定义注入头文本；`includeHeader` 为 true 时生效。 */
  header?: string;
}

/** 按码点截断：超长时保留前 `max` 个字符并追加省略号。 */
export function truncateText(text: string, max: number): string {
  if (!Number.isFinite(max) || max < 0) return text;
  const chars = Array.from(text);
  if (chars.length <= max) return text;
  if (max === 0) return TRUNCATION_SUFFIX;
  return `${chars.slice(0, max).join('')}${TRUNCATION_SUFFIX}`;
}

/** 把单条结果渲染为「[n] 标题 / URL / 摘要」三行块。 */
export function formatSearchResult(
  result: SearchResult,
  index: number,
  maxSnippetChars: number,
): string {
  const title = result.title.trim() || '(无标题)';
  const url = result.url.trim() || '(无链接)';
  const snippet = result.snippet.trim();
  const lines = [`[${index}] ${title}`, `URL: ${url}`];
  if (result.publishedAt) lines.push(`发布时间: ${result.publishedAt}`);
  if (snippet) lines.push(`摘要：${truncateText(snippet, maxSnippetChars)}`);
  return lines.join('\n');
}

/**
 * 把搜索结果渲染成可注入对话的引用文本。
 *
 * 空数组（或全部条目都为空壳）时返回空字符串。
 */
export function formatSearchResults(
  results: readonly SearchResult[],
  options?: FormatSearchResultsOptions,
): string {
  if (!results || results.length === 0) return '';

  const maxSnippetChars = options?.maxSnippetChars ?? DEFAULT_MAX_SNIPPET_CHARS;
  const blocks = results.map((result, i) => formatSearchResult(result, i + 1, maxSnippetChars));

  const body = blocks.join('\n\n');
  if (!options?.includeHeader) return body;

  const header = options.header ?? '以下为联网搜索结果，请结合来源作答并保留 [编号] 引用：';
  return `${header}\n\n${body}`;
}
